import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import {
  ContentManagement,
  ContentManagementVersion,
  ContentManagementTranslation,
  CreateContentManagementPayload,
  CreateContentManagementVersionPayload,
  CreateContentManagementTranslationPayload,
  UpdateContentManagementPayload,
  UpdateContentManagementVersionPayload,
  UpdateContentManagementTranslationPayload,
  ContentManagementResponse,
  ContentManagementVersionResponse,
  ContentManagementTranslationResponse,
} from "../../modules/shared/model/ContentManagementInterfaces";

// Content Management Version Actions
export const createContentManagementVersion = createAsyncThunk<
  ContentManagementVersion,
  CreateContentManagementVersionPayload
>(
  "contentManagement/createVersion",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      const response = await HttpService.post(
        "api/v1/content-management-versions",
        payload,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to create content version";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAllContentManagementVersions = createAsyncThunk<
  ContentManagementVersionResponse,
  { page?: number; limit?: number; isShowLoader?: boolean }
>(
  "contentManagement/getAllVersions",
  async ({ page = 1, limit = 10, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-management-versions?page=${page}&limit=${limit}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch content versions";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getContentManagementVersionById = createAsyncThunk<
  ContentManagementVersion,
  { id: string; isShowLoader?: boolean }
>(
  "contentManagement/getVersionById",
  async ({ id, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-management-versions/${id}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch content version";
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateContentManagementVersion = createAsyncThunk<
  ContentManagementVersion,
  UpdateContentManagementVersionPayload
>(
  "contentManagement/updateVersion",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      const response = await HttpService.patch(
        `api/v1/content-management-versions/${id}`,
        data,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to update content version";
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteContentManagementVersion = createAsyncThunk<
  { id: string },
  { id: string }
>(
  "contentManagement/deleteVersion",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      await HttpService.deleteRecord(
        `api/v1/content-management-versions/${id}`,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return { id };
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to delete content version";
      return rejectWithValue(errorMessage);
    }
  }
);

// Content Management Actions
export const createContentManagement = createAsyncThunk<
  ContentManagement,
  CreateContentManagementPayload
>(
  "contentManagement/createContent",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      const response = await HttpService.post(
        "api/v1/content-managements",
        payload,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to create content management";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAllContentManagements = createAsyncThunk<
  ContentManagementResponse,
  { page?: number; limit?: number; isShowLoader?: boolean }
>(
  "contentManagement/getAllContents",
  async ({ page = 1, limit = 10, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-managements?page=${page}&limit=${limit}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch content managements";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getContentManagementById = createAsyncThunk<
  ContentManagement,
  { id: string; isShowLoader?: boolean }
>(
  "contentManagement/getContentById",
  async ({ id, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-managements/${id}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch content management";
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateContentManagement = createAsyncThunk<
  ContentManagement,
  UpdateContentManagementPayload
>(
  "contentManagement/updateContent",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      const response = await HttpService.patch(
        `api/v1/content-managements/${id}`,
        data,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to update content management";
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteContentManagement = createAsyncThunk<
  { id: string },
  { id: string }
>(
  "contentManagement/deleteContent",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      await HttpService.deleteRecord(
        `api/v1/content-managements/${id}`,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return { id };
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to delete content management";
      return rejectWithValue(errorMessage);
    }
  }
);

// Content Management Translation Actions
export const createContentManagementTranslation = createAsyncThunk<
  ContentManagementTranslation,
  CreateContentManagementTranslationPayload
>(
  "contentManagement/createTranslation",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      const response = await HttpService.post(
        "api/v1/content-management-translations",
        payload,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to create content translation";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAllContentManagementTranslations = createAsyncThunk<
  ContentManagementTranslationResponse,
  { page?: number; limit?: number; isShowLoader?: boolean }
>(
  "contentManagement/getAllTranslations",
  async ({ page = 1, limit = 10, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-management-translations?page=${page}&limit=${limit}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch content translations";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getContentManagementTranslationById = createAsyncThunk<
  ContentManagementTranslation,
  { id: string; isShowLoader?: boolean }
>(
  "contentManagement/getTranslationById",
  async ({ id, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-management-translations/${id}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch content translation";
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateContentManagementTranslation = createAsyncThunk<
  ContentManagementTranslation,
  UpdateContentManagementTranslationPayload
>(
  "contentManagement/updateTranslation",
  async ({ id, data }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      const response = await HttpService.patch(
        `api/v1/content-management-translations/${id}`,
        data,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to update content translation";
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteContentManagementTranslation = createAsyncThunk<
  { id: string },
  { id: string }
>(
  "contentManagement/deleteTranslation",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      await HttpService.deleteRecord(
        `api/v1/content-management-translations/${id}`,
        { "Content-Type": "application/json" },
        true
      );
      dispatch(hideLoader());
      return { id };
    } catch (error: any) {
      dispatch(hideLoader());
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to delete content translation";
      return rejectWithValue(errorMessage);
    }
  }
);

// Get translations by content management type
export const getTranslationsByContentType = createAsyncThunk<
  ContentManagementTranslation[],
  { contentManagementTypeId: string; isShowLoader?: boolean }
>(
  "contentManagement/getTranslationsByContentType",
  async ({ contentManagementTypeId, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-management-translations/content-type/${contentManagementTypeId}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch translations by content type";
      return rejectWithValue(errorMessage);
    }
  }
);

// Get content management by type
export const getContentManagementByType = createAsyncThunk<
  ContentManagement[],
  { type: number; isShowLoader?: boolean }
>(
  "contentManagement/getContentByType",
  async ({ type, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-managements/type/${type}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch content by type";
      return rejectWithValue(errorMessage);
    }
  }
);

// Get content management by version ID
export const getContentManagementByVersionId = createAsyncThunk<
  ContentManagement[],
  { versionId: string; isShowLoader?: boolean }
>(
  "contentManagement/getContentByVersionId",
  async ({ versionId, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-managements/version/${versionId}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch content by version ID";
      return rejectWithValue(errorMessage);
    }
  }
);

// Get content management translations by content management ID
export const getContentManagementTranslationsByContentId = createAsyncThunk<
  ContentManagementTranslation[],
  { contentId: string; isShowLoader?: boolean }
>(
  "contentManagement/getTranslationsByContentId",
  async ({ contentId, isShowLoader = true }, { dispatch, rejectWithValue }) => {
    try {
      if (isShowLoader) {
        dispatch(showLoader());
      }
      const response = await HttpService.get(
        `api/v1/content-management-translations/content-management/${contentId}`,
        true,
        {}
      );
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      return response;
    } catch (error: any) {
      if (isShowLoader) {
        dispatch(hideLoader());
      }
      const errorData = error?.data?.errors;
      const errorMessage = errorData?.message || "Failed to fetch translations by content ID";
      return rejectWithValue(errorMessage);
    }
  }
);
